import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { assistant as assistantApi } from "../../api/resources";
import { ApiError } from "../../api/client";
import { MarkdownLite } from "../../lib/markdownLite";
import type {
  AssistantAvailability,
  AssistantConversation,
  AssistantMessage,
  AssistantPendingConfirmation,
  AssistantTurn,
} from "../../lib/types";
import { ErrorBanner, Loading } from "../ui";

/**
 * The assistant, as a panel beside the work rather than a page away from it.
 *
 * Three things this component is careful about, because they are the product:
 *
 *  - Every assistant message is labelled **AI suggestion — not confirmed**. There is no state in
 *    which the panel presents model output as settled.
 *  - A drafted answer is never applied. The panel shows it with Use draft / Edit / Discard, and
 *    only the button calls the confirm route.
 *  - What a turn cost and which prompt version produced it are one click away, not hidden.
 */

export interface AssistantPanelProps {
  organizationId: string;
  /** Where the person is, so the assistant reads the right thing without being told. */
  mode?: string;
  interviewId?: string;
  contextTaskId?: string;
  questionSemanticId?: string;
  operation?: string;
  suggestions?: string[];
  title?: string;
  onClose?: () => void;
  /** Called after a draft is saved, so the surrounding page can refresh. */
  onDraftSaved?: (semanticId: string, value: unknown) => void;
}

const DEFAULT_SUGGESTIONS = [
  "What does this question mean?",
  "Why are you asking this?",
  "Help me phrase my answer",
];

const LABEL = "AI suggestion — not confirmed";

function money(amount: string | null, currency: string, priced: boolean): string {
  if (!priced) return "unpriced";
  if (amount === null) return "unknown";
  const value = Number(amount);
  if (Number.isNaN(value)) return amount;
  return `${currency === "USD" ? "$" : `${currency} `}${value.toFixed(value < 1 ? 4 : 2)}`;
}

export function AssistantPanel({
  organizationId,
  mode = "general_chat",
  interviewId,
  contextTaskId,
  questionSemanticId,
  operation,
  suggestions = DEFAULT_SUGGESTIONS,
  title = "Discovery Assistant",
  onClose,
  onDraftSaved,
}: AssistantPanelProps) {
  const [availability, setAvailability] = useState<AssistantAvailability>();
  const [conversation, setConversation] = useState<AssistantConversation>();
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [turns, setTurns] = useState<Record<string, AssistantTurn>>({});
  const [pending, setPending] = useState<AssistantPendingConfirmation | null>(null);
  const [draftEdit, setDraftEdit] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [explaining, setExplaining] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    assistantApi
      .availability(organizationId)
      .then((body) => !cancelled && setAvailability(body))
      .catch(() => !cancelled && setAvailability(undefined));
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  useEffect(() => {
    // Optional call: not every environment implements scrollIntoView, and failing to scroll
    // must never take the panel down.
    endRef.current?.scrollIntoView?.({ behavior: "smooth", block: "end" });
  }, [messages, pending]);

  const ensureConversation = useCallback(async (): Promise<AssistantConversation> => {
    if (conversation) return conversation;
    const created = await assistantApi.createConversation(organizationId, {
      mode,
      linked_interview_id: interviewId ?? null,
      linked_context_task_id: contextTaskId ?? null,
      linked_question_semantic_id: questionSemanticId ?? null,
    });
    setConversation(created);
    return created;
  }, [conversation, organizationId, mode, interviewId, contextTaskId, questionSemanticId]);

  const ask = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || busy) return;
      setBusy(true);
      setError(undefined);
      setInput("");
      try {
        const active = await ensureConversation();
        const turn = await assistantApi.turn(organizationId, active.id, {
          text: trimmed,
          operation,
        });
        setMessages((current) => [...current, turn.user_message, turn.assistant_message]);
        setTurns((current) => ({ ...current, [turn.assistant_message.id]: turn }));
        setPending(turn.pending_confirmation);
        setDraftEdit(
          turn.pending_confirmation
            ? String(turn.pending_confirmation.arguments.value ?? "")
            : null,
        );
      } catch (caught) {
        // A provider that is down is said plainly. The panel never fabricates an answer.
        setError(
          caught instanceof ApiError
            ? caught.message
            : "The assistant could not answer just now.",
        );
      } finally {
        setBusy(false);
      }
    },
    [busy, ensureConversation, operation, organizationId],
  );

  const useDraft = useCallback(async () => {
    if (!pending || !conversation) return;
    setBusy(true);
    setError(undefined);
    try {
      const args = { ...pending.arguments };
      if (draftEdit !== null) args.value = draftEdit;
      const row = await assistantApi.confirmToolCall(
        organizationId,
        conversation.id,
        pending.tool_call_id,
        args,
      );
      if (row.status === "confirmed") {
        onDraftSaved?.(String(args.semantic_id ?? questionSemanticId ?? ""), args.value);
        setPending(null);
        setDraftEdit(null);
      } else {
        setError(row.error ?? "That could not be saved.");
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "That could not be saved.");
    } finally {
      setBusy(false);
    }
  }, [pending, conversation, draftEdit, organizationId, onDraftSaved, questionSemanticId]);

  const discardDraft = useCallback(async () => {
    if (!pending || !conversation) return;
    await assistantApi
      .declineToolCall(organizationId, conversation.id, pending.tool_call_id)
      .catch(() => undefined);
    setPending(null);
    setDraftEdit(null);
  }, [pending, conversation, organizationId]);

  const rate = useCallback(
    async (messageId: string, rating: number) => {
      await assistantApi
        .feedback(organizationId, messageId, { rating })
        .catch(() => undefined);
      setTurns((current) => ({ ...current }));
    },
    [organizationId],
  );

  if (availability && !availability.available) {
    return (
      <aside className="assistant" aria-label={title}>
        <header className="assistant__header">
          <strong>{title}</strong>
          {onClose ? (
            <button type="button" className="btn btn--ghost btn--small" onClick={onClose}>
              Close
            </button>
          ) : null}
        </header>
        <div className="assistant__body">
          <div className="banner banner--warn">
            {availability.reason ?? "The assistant is not available here."}
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside className="assistant" aria-label={title}>
      <header className="assistant__header">
        <strong>{title}</strong>
        {onClose ? (
          <button type="button" className="btn btn--ghost btn--small" onClick={onClose}>
            Close
          </button>
        ) : null}
      </header>

      <div className="assistant__body">
        {!availability ? <Loading label="Checking the assistant…" /> : null}

        {messages.length === 0 && availability?.available ? (
          <div className="assistant__intro">
            <p className="small muted">
              I can explain a question, find what has already been said, and help you put your
              answer into words. I never answer for you, and nothing I say is confirmed until
              you confirm it.
            </p>
            <div className="assistant__suggestions">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  className="chip"
                  onClick={() => ask(suggestion)}
                  disabled={busy}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {messages.map((message) => {
          const turn = turns[message.id];
          const isAssistant = message.role === "assistant";
          const failed =
            message.message_type === "error" || message.message_type === "refusal";
          return (
            <div
              key={message.id}
              className={`assistant__msg assistant__msg--${message.role}${
                failed ? " is-error" : ""
              }`}
            >
              {isAssistant ? (
                <div className="assistant__msg-label">{failed ? "Could not answer" : LABEL}</div>
              ) : null}
              <MarkdownLite
                text={message.content_text}
                renderCitation={(reference) =>
                  reference.startsWith("ragflow://") ? (
                    <span className="cite" title={reference}>
                      document
                    </span>
                  ) : (
                    <Link className="cite" to={`/knowledge?fact=${reference}`}>
                      what we heard
                    </Link>
                  )
                }
              />
              {isAssistant && turn ? (
                <div className="assistant__msg-foot">
                  <button
                    type="button"
                    className="btn btn--ghost btn--tiny"
                    onClick={() =>
                      setExplaining(explaining === message.id ? null : message.id)
                    }
                  >
                    How this was generated
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--tiny"
                    onClick={() => rate(message.id, 1)}
                    aria-label="Helpful"
                  >
                    Helpful
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--tiny"
                    onClick={() => rate(message.id, -1)}
                    aria-label="Not helpful"
                  >
                    Not helpful
                  </button>
                </div>
              ) : null}
              {isAssistant && turn && explaining === message.id ? (
                <dl className="assistant__explain">
                  <dt>Model</dt>
                  <dd className="mono">{turn.model}</dd>
                  <dt>Prompt</dt>
                  <dd>{turn.prompt_source ?? "—"}</dd>
                  <dt>Tools used</dt>
                  <dd>
                    {turn.tool_calls.length === 0
                      ? "none"
                      : turn.tool_calls
                          .map((call) => `${call.tool_name} (${call.status})`)
                          .join(", ")}
                  </dd>
                  <dt>Tokens</dt>
                  <dd>
                    {turn.input_tokens === null && turn.output_tokens === null
                      ? "not reported"
                      : `${turn.input_tokens ?? "?"} in / ${turn.output_tokens ?? "?"} out`}
                  </dd>
                  <dt>Cost</dt>
                  <dd>{money(turn.cost_amount, turn.currency, turn.priced)}</dd>
                  <dt>Latency</dt>
                  <dd>{turn.latency_ms === null ? "—" : `${turn.latency_ms} ms`}</dd>
                </dl>
              ) : null}
            </div>
          );
        })}

        {pending ? (
          <div className="assistant__draft">
            <div className="assistant__msg-label">AI draft — nothing is saved yet</div>
            <textarea
              rows={3}
              value={draftEdit ?? ""}
              onChange={(event) => setDraftEdit(event.target.value)}
              aria-label="Draft answer"
            />
            <p className="tiny muted">{pending.note}</p>
            <div className="assistant__draft-actions">
              <button type="button" className="btn" onClick={useDraft} disabled={busy}>
                Use draft
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={discardDraft}
                disabled={busy}
              >
                Discard
              </button>
            </div>
          </div>
        ) : null}

        {busy ? <Loading label="Thinking…" /> : null}
        <ErrorBanner error={error} />
        <div ref={endRef} />
      </div>

      <form
        className="assistant__composer"
        onSubmit={(event) => {
          event.preventDefault();
          ask(input);
        }}
      >
        <textarea
          rows={2}
          placeholder="Ask about this question…"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              ask(input);
            }
          }}
          disabled={busy || availability?.available === false}
          aria-label="Ask the assistant"
        />
        <button type="submit" className="btn" disabled={busy || !input.trim()}>
          Ask
        </button>
      </form>
    </aside>
  );
}
